import { useState } from 'react';
import { Collapse, Flex, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import FigmaDeleteTrashRedIcon from 'components/Icons/FigmaDeleteTrashRedIcon';
import FigmaMoveToFolderIcon from 'components/Icons/FigmaMoveToFolderIcon';
import {
  DeleteBulkDimensionItems,
  DeleteBulkDimensionItemsReducer,
  FetchDimensionItems,
  getBaseScenario,
  getSelectedDimension,
  getTableCheckBox,
} from 'redux/BuilderModeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import { neutral, primaryAction, radius } from './designTokens';
import ModernDeleteConfirmModal from './ModernDeleteConfirmModal';
import MoveItemsPopover from './MoveItemsPopover';

/**
 * Own row above the table grid (between the title row and the column
 * headers), matching the Figma "selected" state exactly — plain "N
 * Selected" text on the left, Move/Delete as individual bordered pill
 * buttons on the right. Collapses/expands with the selection state.
 */
const SelectionActionBar = () => {
  const dispatch: AppDispatch = useDispatch();
  const [deleteModal, setDeleteModal] = useState(false);
  const selectedDimension: any = useSelector(getSelectedDimension);
  const selectedScenario = useSelector(getBaseScenario);
  const tableCheckBox = useSelector(getTableCheckBox);

  const selectedIds = tableCheckBox
    .filter((item: any) => item.status === true && item.id !== 'all' && item.id !== 'intermediate')
    .map((item: any) => item.id);
  const selectedCount = selectedIds.length;
  const isOpen = selectedCount > 0;

  // Keeps showing the last real count while the bar is collapsing out, so
  // the label doesn't flash to "0 Selected" mid-animation.
  const [displayCount, setDisplayCount] = useState(selectedCount);
  if (selectedCount > 0 && selectedCount !== displayCount) setDisplayCount(selectedCount);

  const handleDelete = async () => {
    setDeleteModal(false);
    await dispatch(DeleteBulkDimensionItemsReducer({ item_id: selectedIds }));
    await dispatch(
      DeleteBulkDimensionItems({
        request_body: { item_id: selectedIds, scenario_id: selectedScenario?.id },
      }),
    );
    await dispatch(
      FetchDimensionItems({
        selectedId: selectedDimension?.id,
        parameters: { scenario_id: selectedScenario?.id },
      }),
    );
  };

  return (
    <>
      <Collapse in={isOpen} animateOpacity>
        <Flex alignItems='center' gap='12px' mb='12px' pt='2px'>
          <Text color={primaryAction.bg} fontSize='13px' fontWeight={500}>
            {displayCount} Selected
          </Text>

          <MoveItemsPopover
            onSelectDimension={(dimension) => {
              // No backend endpoint yet to move items across dimensions —
              // this reports the chosen target so the call can be wired up
              // as soon as one exists.
              // eslint-disable-next-line no-console
              console.log('Move selected items to dimension:', dimension, selectedIds);
            }}
          >
            <Flex
              as='button'
              type='button'
              alignItems='center'
              gap='6px'
              bg='white'
              border='1px solid'
              borderColor={neutral.border}
              borderRadius={radius.control}
              px='10px'
              py='6px'
              cursor='pointer'
              transition='background 0.12s ease, border-color 0.12s ease, transform 0.1s ease'
              _hover={{ bg: neutral.surfaceHover, borderColor: neutral.borderStrong }}
              _active={{ bg: neutral.surfaceHover, transform: 'scale(0.94)' }}
            >
              <FigmaMoveToFolderIcon width={13} height={13} color={primaryAction.bg} />
              <Text color={primaryAction.bg} fontSize='13px' fontWeight={500}>
                Move
              </Text>
            </Flex>
          </MoveItemsPopover>

          <Flex
            as='button'
            type='button'
            alignItems='center'
            gap='6px'
            bg='white'
            border='1px solid'
            borderColor={neutral.border}
            borderRadius={radius.control}
            px='10px'
            py='6px'
            cursor='pointer'
            transition='background 0.12s ease, border-color 0.12s ease, transform 0.1s ease'
            _hover={{ bg: neutral.surfaceHover, borderColor: neutral.borderStrong }}
            _active={{ bg: neutral.surfaceHover, transform: 'scale(0.94)' }}
            onClick={() => setDeleteModal(true)}
          >
            <FigmaDeleteTrashRedIcon width={11} height={13} />
            <Text color='#FE552F' fontSize='13px' fontWeight={500}>
              Delete
            </Text>
          </Flex>
        </Flex>
      </Collapse>

      <ModernDeleteConfirmModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        itemName={selectedCount > 1 ? `${selectedCount} rows` : `${selectedCount} row`}
        onDelete={handleDelete}
        orgChart
      />
    </>
  );
};

export default SelectionActionBar;
